import { useState, useCallback, useRef, useEffect } from 'react';
import { VariationChain, VariationNode, SpawnStrategy } from '@/types/variationChain.types';
import { triggerVariation, pollClipsByIds } from '@services/sunoApi';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

function buildTree(nodes: VariationNode[]): VariationNode[] {
  const map = new Map<string, VariationNode>();
  const roots: VariationNode[] = [];

  nodes.forEach(n => map.set(n.id, { ...n, children: [] }));
  nodes.forEach(n => {
    const node = map.get(n.id)!;
    if (n.parentNodeId && map.has(n.parentNodeId)) {
      map.get(n.parentNodeId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

export function useVariationChains() {
  const [chains, setChains] = useState<VariationChain[]>([]);
  const [activeChain, setActiveChain] = useState<VariationChain | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Map of pollKey -> cancel fn for in-flight clip polls.
  const pollingRefs = useRef<Map<string, () => void>>(new Map());

  // Cancel all in-flight clip polls on unmount so the recursive Suno polling
  // (and its retained callbacks) don't leak/accumulate across a long session.
  useEffect(() => {
    const polls = pollingRefs.current;
    return () => {
      polls.forEach(cancel => cancel?.());
      polls.clear();
    };
  }, []);

  const fetchChains = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithCsrf(`${API_ENDPOINTS.variations.list}?status=active`);
      const data = await res.json();
      setChains(data.chains || []);
    } catch (err) {
      console.error('Failed to fetch chains:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchChain = useCallback(async (id: string) => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.variations.get(id));
      if (!res.ok) return null;
      const chain = await res.json();
      setActiveChain(chain);
      return chain;
    } catch (err) {
      console.error('Failed to fetch chain:', err);
      return null;
    }
  }, []);

  const createChain = useCallback(async (
    name: string,
    rootNode: { sunoClipId?: string; title?: string; prompt?: string; tags?: string; negativeTags?: string; audioUrl?: string }
  ): Promise<VariationChain | null> => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.variations.create, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, rootClipId: rootNode.sunoClipId || null })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('Chain creation failed:', res.status, errData);
        return null;
      }
      const chain = await res.json();

      // Add root node — always 'complete' since it represents the existing output
      const nodeRes = await fetchWithCsrf(API_ENDPOINTS.variations.nodes(chain.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sunoClipId: rootNode.sunoClipId || null,
          status: 'complete',
          generation: 1,
          title: rootNode.title || 'Root',
          prompt: rootNode.prompt || null,
          tags: rootNode.tags || null,
          negativeTags: rootNode.negativeTags || null,
          audioUrl: rootNode.audioUrl || null,
        })
      });
      if (!nodeRes.ok) {
        const errData = await nodeRes.json().catch(() => ({}));
        console.error('Root node creation failed:', nodeRes.status, errData);
        return null;
      }
      const rootNodeData = await nodeRes.json();

      // Fetch the full chain — but don't fail if this step errors
      const fullChain = await fetchChain(chain.id);
      if (fullChain) return fullChain;

      // Fallback: construct chain from the data we already have
      const fallbackChain: VariationChain = {
        ...chain,
        nodes: [rootNodeData],
      };
      setActiveChain(fallbackChain);
      return fallbackChain;
    } catch (err) {
      console.error('Failed to create chain:', err);
      return null;
    }
  }, [fetchChain]);

  const deleteChain = useCallback(async (id: string) => {
    try {
      await fetchWithCsrf(API_ENDPOINTS.variations.delete(id), { method: 'DELETE' });
      setChains(prev => prev.filter(c => c.id !== id));
      if (activeChain?.id === id) setActiveChain(null);
    } catch (err) {
      console.error('Failed to delete chain:', err);
    }
  }, [activeChain]);

  const spawnVariations = useCallback(async (
    chainId: string,
    parentNodeId: string,
    count: number,
    strategy: SpawnStrategy,
    prompt: string,
    tags: string,
    negativeTags: string,
    title: string
  ) => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.variations.spawn(chainId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentNodeId, count, strategy, prompt, tags, negativeTags, title })
      });
      const data = await res.json();
      // Refresh the chain to get updated nodes
      await fetchChain(chainId);
      return data.nodes as VariationNode[];
    } catch (err) {
      console.error('Failed to spawn variations:', err);
      return [];
    }
  }, [fetchChain]);

  const rateNode = useCallback(async (chainId: string, nodeId: string, rating: number) => {
    try {
      await fetchWithCsrf(API_ENDPOINTS.variations.rateNode(chainId, nodeId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overallRating: rating })
      });
      await fetchChain(chainId);
    } catch (err) {
      console.error('Failed to rate node:', err);
    }
  }, [fetchChain]);

  const updateNodeStatus = useCallback(async (
    chainId: string,
    nodeId: string,
    status: string,
    extra?: { sunoClipId?: string; audioUrl?: string; title?: string }
  ) => {
    try {
      await fetchWithCsrf(API_ENDPOINTS.variations.updateNodeStatus(chainId, nodeId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, ...extra })
      });
      await fetchChain(chainId);
    } catch (err) {
      console.error('Failed to update node status:', err);
    }
  }, [fetchChain]);

  const triggerNodeGeneration = useCallback(async (
    chainId: string,
    node: VariationNode,
    token: string
  ): Promise<string | null> => {
    if (!node.prompt || !node.tags) return 'Node missing prompt or tags';

    const model = localStorage.getItem('suno_model') || 'chirp-hawk';

    // Parse configTweak — stored as JSON string in DB
    let tweak: { strategy?: string; continueClipId?: string; styleWeight?: number; weirdness?: number } = { strategy: 'native' };
    if (node.configTweak) {
      if (typeof node.configTweak === 'string') {
        try { tweak = JSON.parse(node.configTweak as unknown as string); } catch { /* use default */ }
      } else {
        tweak = node.configTweak;
      }
    }
    const continueClipId = tweak.continueClipId || null;

    try {
      // Mark as generating
      await updateNodeStatus(chainId, node.id, 'generating');

      const result = await triggerVariation(
        {
          prompt: node.prompt,
          tags: node.tags,
          negativeTags: node.negativeTags || '',
          title: node.title || 'Variation'
        },
        token,
        model,
        continueClipId,
        {
          styleStrength: tweak.styleWeight,
          weirdness: tweak.weirdness
        }
      );

      // Extract clip IDs from response
      const clips = result?.clips || result || [];
      const clipArr = Array.isArray(clips) ? clips : [clips];
      const clipId = clipArr[0]?.id;

      if (clipId) {
        await updateNodeStatus(chainId, node.id, 'generating', { sunoClipId: clipId });

        // Start cancellable polling for this clip. The cancel fn is stored so
        // the poll is torn down on completion AND on unmount (see cleanup effect
        // above) — the old pollClipStatus had neither and leaked per run.
        const pollKey = `${chainId}:${node.id}`;
        if (!pollingRefs.current.get(pollKey)) {
          const stop = () => {
            pollingRefs.current.get(pollKey)?.();
            pollingRefs.current.delete(pollKey);
          };

          const cancel = pollClipsByIds(token, [clipId], async (clips) => {
            const clip = clips.find(c => c.id === clipId);

            if (clip?.status === 'complete' && clip.audio_url) {
              await updateNodeStatus(chainId, node.id, 'complete', {
                audioUrl: clip.audio_url,
                title: clip.title || node.title || undefined
              });
              stop();
            } else if (clip?.status === 'error') {
              await updateNodeStatus(chainId, node.id, 'failed');
              stop();
            } else if (clips.length === 0) {
              // maxAttempts timeout (or clip not found) — matches prior behavior.
              await updateNodeStatus(chainId, node.id, 'failed');
              stop();
            }
          }, { interval: 10000, maxAttempts: 30 });

          pollingRefs.current.set(pollKey, cancel);
        }
        return null; // success
      } else {
        await updateNodeStatus(chainId, node.id, 'failed');
        return 'Suno API returned no clip ID — check credits or token';
      }
    } catch (err: unknown) {
      console.error('Node generation failed:', err);
      await updateNodeStatus(chainId, node.id, 'failed');
      return err instanceof Error ? err.message : 'Unknown generation error';
    }
  }, [updateNodeStatus]);

  const getTree = useCallback((): VariationNode[] => {
    if (!activeChain) return [];
    return buildTree(activeChain.nodes);
  }, [activeChain]);

  return {
    chains,
    activeChain,
    isLoading,
    fetchChains,
    fetchChain,
    createChain,
    deleteChain,
    spawnVariations,
    rateNode,
    updateNodeStatus,
    triggerNodeGeneration,
    getTree,
    setActiveChain
  };
}
